import React from "react"
import { useTranslation } from "react-i18next"

import * as UserApi from "../../api/UserApi"
import * as AuthApi from "features/auth/api/AuthApi"
import * as BikeApi from "features/bike/api/BikeApi"

import { formStatusEnum, FormElement } from "components/Form"
import { Spinner, Button } from "components/Elements"

import { useForm, useAlert, useOperator } from "hooks"
import { User, UserData, ErrorResponse } from "models"
import { IS_FNUCI_SYNC, PRIVATE_GENDER_VALUE } from "utils/constants"
import { useBikesStore } from "features/dashboard/hooks"

type UserCredentialsProps = {
	userData: Partial<User & UserData>
	refreshUser: () => Promise<void>
}

const defaultFormFields = {
	newMail: "",
	code: "",
}

export const UserCredentials: React.FC<UserCredentialsProps> = ({ userData, refreshUser }) => {
	const { bikesStore, fetchBikes } = useBikesStore()
	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: defaultFormFields,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const [hasReceivedCode, setHasReceivedCode] = React.useState<boolean>(false)
	const isLoading = formStatus === formStatusEnum.loading
	const isReadonly = formStatus === formStatusEnum.readonly
	const isReady = formStatus === formStatusEnum.ready

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { currentOperator } = useOperator()

	React.useEffect(() => {
		// We fetch bikes
		if (!bikesStore.bikes) fetchBikes()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const resetForm = (event?: React.MouseEvent<HTMLButtonElement>) => {
		if (event) event.preventDefault()
		setFormStatus(formStatusEnum.readonly)
		setHasReceivedCode(false)
		setShowLocalErrors(false)
		setFormFields(defaultFormFields)
	}

	const requestCode = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const { getError } = await AuthApi.signUpCheckMail(
					formFields.newMail,
					currentOperator?.id
				)
				if (getError()) throw getError()
				setHasReceivedCode(true)
				alert("success", t("code_send", { ns: "form" }))
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const resendCode = async () => {
		try {
			setFormStatus(formStatusEnum.loading)

			const { getError } = await AuthApi.signUpCheckMail(
				formFields.newMail,
				currentOperator?.id
			)
			if (getError()) throw getError()
			alert("success", t("code_resend", { ns: "form" }))
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		} finally {
			setFormStatus(formStatusEnum.ready)
		}
	}

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				// 1 --------------------------- BIKE UPDATE - FNUCI ------------------------
				// --------------------------------------------------------------------------
				// We need to update BIKE OWNER because user's email changed

				// First, we fetch bikes
				// ---------------------
				const { res: fetchedBikes, getError: getBikesError } = await BikeApi.getBikes()
				if (getBikesError) throw getBikesError()
				if (!fetchedBikes) throw getBikesError()

				// Then, we exclude all bikes who need an "action" :
				// - bikes displayed in Notifications' area
				// - Or bikes not sync with FNUCI
				// -------------------------------------------------
				const filteredBikes = fetchedBikes.filter(
					(bike) => !bike.bike_action_needed && bike.is_sync_with_fnuci === IS_FNUCI_SYNC
				)

				// Finally, we update each bike
				// ----------------------------
				const userDescription = userData.description

				const bikeOwner = {
					gender: userDescription.gender,
					phone: userDescription.phone,
					mail: formFields.newMail.trim(),
					...(() => {
						if (userDescription.gender === PRIVATE_GENDER_VALUE) {
							return {
								first_name: userDescription.first_name,
								last_name: userDescription.last_name,
							}
						} else {
							return {
								social_reason: userDescription.social_reason,
							}
						}
					})(),
				}

				for (const bike of filteredBikes) {
					const updatedBikePayload = {
						bike_id: bike.bike_id,
						row_version: bike.row_version,
						bike_owner: bikeOwner,
					}

					const { getError: getUpdateBikeError } = await BikeApi.updateBike(
						updatedBikePayload
					)
					if (getUpdateBikeError()) throw getUpdateBikeError()
				}

				// 2 --------------------------- EMAIL UPDATE -------------------------------
				// --------------------------------------------------------------------------
				const newUserEmail = {
					account_id: userData.account_id,
					row_version: userData.row_version,
					mail: formFields.newMail.trim(),
					code: formFields.code,
				}

				const { getError } = await UserApi.updateUser(newUserEmail)
				if (getError()) throw getError()

				alert("success", t("info_update", { ns: "form" }))
				// refresh and reset
				await refreshUser()
				resetForm()
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.readonly)
			}
		}
	}

	const isBikeActionNeeded = bikesStore && bikesStore?.bikesWithActionNeeded?.length > 0

	return (
		<div className="c-block px-4 lg:px-8 py-6 md:py-10">
			<h2 className="c-block__title text-2xl -top-6">{t("my_credential", { ns: "user" })}</h2>

			{isBikeActionNeeded && (
				<p className="c-block__intro c-block__intro--alert mb-6 py-2">
					{t("bike_state.prevent_email_modifying", {
						ns: "bike",
					})}
				</p>
			)}

			<form className="mt-7 md:mt-0">
				<p>{t("current_email", { ns: "user" })}</p>
				<p className="font-bold text-s">{userData?.description?.mail}</p>

				{!isReadonly && (
					<FormElement
						value={formFields.newMail}
						name="newMail"
						type="email"
						id="mail"
						label={t("new_email", { ns: "user" })}
						className="mt-5 mb-6"
						required={true}
						showErrors={showLocalErrors}
						onValidate={handleValidation}
						onChange={handleChange}
						disabled={hasReceivedCode}
						forceFloating={true}
					/>
				)}

				{hasReceivedCode && (
					<>
						<p>{t("received_code_email", { ns: "user" })}</p>

						<div className="grid justify-start">
							<FormElement
								value={formFields.code}
								name="code"
								type="code"
								id="code"
								className="mb-1 c-input-code-verification--s"
								required={true}
								showErrors={showLocalErrors}
								onValidate={handleValidation}
								onChange={handleChange}
							/>
							<button
								type="button"
								onClick={resendCode}
								className="underline text-xxs justify-self-end"
							>
								{t("resend_code", { ns: "user" })}
							</button>
						</div>
					</>
				)}

				{isReadonly && (
					<Button
						variant="primary"
						type="submit"
						onClick={() => setFormStatus(formStatusEnum.ready)}
						className="px-10 py-1 mt-6"
						size="small"
						disabled={isBikeActionNeeded}
					>
						{t("global.edit")}
					</Button>
				)}

				{isReady && (
					<div className="flex items-center mt-8 justify-center md:justify-start">
						{hasReceivedCode && (
							<Button
								variant="secondary"
								type="submit"
								onClick={handleSubmit}
								className="mx-1 px-10 py-1 lg:mx-3"
								size="small"
							>
								{t("global.validate")}
							</Button>
						)}
						{!hasReceivedCode && (
							<Button
								variant="secondary"
								type="submit"
								onClick={requestCode}
								className="mx-1 px-10 py-1 lg:mx-3"
								size="small"
							>
								{t("global.continue")}
							</Button>
						)}
						<Button
							variant="secondaryBordered"
							type="button"
							onClick={resetForm}
							className="mx-1 px-10 py-1 lg:mx-3"
							size="small"
						>
							{t("global.cancel")}
						</Button>
					</div>
				)}

				{isLoading && (
					<div className="flex justify-center my-8">
						<Spinner color={"var(--primaryColor)"} size={"100px"} />
					</div>
				)}
			</form>
		</div>
	)
}
